import { Select } from '@customerio/pluma-components/react';
import type { AttributeNamePickerProps } from '../components/attribute-row/attribute-row';

/**
 * The select an attribute row names its attribute with, for any surface whose
 * names arrive as a searched list.
 *
 * Shared because the *behaviour* is the same wherever the names come from: the
 * row controls the search input, which turns Pluma's own client-side filter
 * off, so each caller's hook narrows the list and this select only renders it.
 * Two rules follow from that arrangement and are the reason this is one
 * component rather than a pattern each picker repeats:
 *
 * - the term is cleared on select and on close, because a term left behind is
 *   invisible in the reopened input yet still seeds the custom option, so later
 *   keystrokes would append to it;
 * - closing the dropdown is what marks the picker visited, so a freshly added
 *   row does not open already showing its "select an attribute" error.
 *
 * What differs per surface is only what the control is called and where its
 * options came from, so those arrive as props.
 */

/** The options and search state a picker's own hook supplies. */
export type AttributeNameSearchState = {
  options: { value: string; label: string }[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  /** Whether options are still arriving, which the select shows as loading. */
  isLoading: boolean;
};

export type AttributeNameSelectProps = AttributeNamePickerProps & {
  /** The options and search state, from the surface's own names hook. */
  names: AttributeNameSearchState;
  /** What this kind of attribute is called, used as the control's label. */
  kindLabel: string;
  /** What the closed select says while no attribute is named; the kind label by default. */
  placeholder?: string;
  /** What the search input says while empty. */
  searchPlaceholder?: string;
  /** Distinguishes this surface's control in tests. */
  testId: string;
};

export function AttributeNameSelect({
  field,
  error,
  isDisabled,
  onFieldChange,
  onTouched,
  names,
  kindLabel,
  placeholder = kindLabel,
  searchPlaceholder = 'Find or specify an attribute',
  testId,
}: AttributeNameSelectProps) {
  return (
    <Select
      data-testid={testId}
      ariaLabel={kindLabel}
      size="sm"
      placeholder={placeholder}
      options={names.options}
      value={field === '' ? undefined : field}
      error={error}
      isDisabled={isDisabled}
      isSearchable
      searchPlaceholder={searchPlaceholder}
      searchInputValue={names.searchTerm}
      onSearchInput={(event) => names.setSearchTerm(event.target.value)}
      isLoading={names.isLoading}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          onTouched();
          names.setSearchTerm('');
        }
      }}
      onChange={(value: string | null) => {
        names.setSearchTerm('');
        if (value != null) {
          onFieldChange(value);
        }
      }}
    />
  );
}
